// src/components/Shared/IngredientAutocomplete.tsx
import React, { useState, useEffect, useRef } from 'react';
import { fetchIngredientSuggestions } from '../../utils/ingredientApi.ts';

export type Suggestion = {
  externalId: string;
  nameCanonical: string;
  id: string;
  synonyms: string[];
};

export interface IngredientAutocompleteProps {
  /** current text in the box (optional) */
  value?: string;
  /** keystrokes only */
  onInputChange?: (val: string) => void;
  /** selection only */
  onSelect: (sel: { name: string; catalogId: string | null }) => void;
  placeholder?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** when true, after you click a dropdown item the input will clear */
  clearOnSelect?: boolean;
}

export default function IngredientAutocomplete({
  value = '',
  onInputChange,
  onSelect,
  placeholder = 'Ingredient',
  onBlur,
  clearOnSelect = false,
}: IngredientAutocompleteProps) {
  const [input, setInput] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep our internal input in sync if parent clears it
  useEffect(() => {
    setInput(value);
  }, [value]);

  // fetch logic unchanged...
  useEffect(() => {
    if (!open || input.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const deb = setTimeout(async () => {
      const results = await fetchIngredientSuggestions(input.trim());
      // … your scoring/sorting …
      setSuggestions(results);
    }, 200);
    return () => clearTimeout(deb);
  }, [input, open]);

  // click outside → close
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        className="w-full border border-gray-300 ml-1 px-2 py-1 rounded focus:border-black focus:outline-none"
        placeholder={placeholder}
        value={input}
        onChange={(e) => {
          const val = e.target.value;
          setInput(val);
          onInputChange?.(val); // <<< only typing
          setOpen(val.trim().length >= 2);
        }}
        onFocus={() => {
          if (input.trim().length >= 2 && suggestions.length > 0) {
            setOpen(true);
          }
        }}
        onBlur={(e) => {
          setTimeout(() => setOpen(false), 100);
          onBlur?.(e);
        }}
      />

      {open && suggestions.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border rounded mt-1 max-h-40 overflow-auto">
          {suggestions.map((item) => {
            const normalizedInput = input.trim().toLowerCase();
            const normalizedCanonical = item.nameCanonical.toLowerCase();
            const isCanonicalMatch = normalizedCanonical.includes(normalizedInput);
            const matchedSynonym = !isCanonicalMatch
              ? item.synonyms.find((syn) =>
                  syn.toLowerCase().includes(normalizedInput)
                )
              : null;

            return (
              <li
                key={item.externalId}
                className="px-2 py-1 cursor-pointer hover:bg-gray-300 odd:bg-white even:bg-gray-200 border-1 border-b border-gray-700"
                onClick={() => {
                  setOpen(false);
                  if (clearOnSelect) setInput('');
                  onSelect({
                    name: item.nameCanonical,
                    catalogId: item.id,
                  });
                }}
              >
                <span>{item.nameCanonical}</span>
                {matchedSynonym && (
                  <span className="text-gray-500"> ({matchedSynonym})</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
